import type { CorrespondanceContact, PasPourLuiProfil, PrevenirProfil } from '@artosera/shared';
import { Button, Checkbox, Dialog, Select, Textarea } from '@artosera/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { profilsApi, useAPrevenir, useGesteProfil } from '../../api/profils.js';
import { estApiError } from '../../api/errors.js';
import { galerieALaction, galerieAModule } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { Field } from '../Field.js';
import { RetourGeste } from '../RetourGeste.js';
import { RaisonsCorrespondance } from './RaisonsCorrespondance.js';
import styles from './Profils.module.css';

export function APrevenirOeuvre({ oeuvreId }: { oeuvreId: string }) {
  const { galerie } = useShellContext();
  if (
    !galerieAModule(galerie, 'profils-collectionneurs') ||
    !galerieALaction(galerie, 'profils:read')
  )
    return null;
  return <APrevenir key={oeuvreId} oeuvreId={oeuvreId} />;
}

function APrevenir({ oeuvreId }: { oeuvreId: string }) {
  const { t } = useTranslation('regie');
  const { galerieSlug, galerie } = useShellContext();
  const liste = useAPrevenir(galerieSlug, oeuvreId);
  const recalcul = useGesteProfil(galerieSlug, (_corps: void, appel) =>
    profilsApi.recalculerCorrespondances(galerieSlug, oeuvreId, appel),
  );
  const [geste, setGeste] = useState<{
    contact: CorrespondanceContact;
    type: 'prevenu' | 'pasPourLui';
  } | null>(null);
  const [traites, setTraites] = useState<string[]>([]);
  const [retour, setRetour] = useState<string | null>(null);
  const peutEcrire = galerieALaction(galerie, 'profils:write');
  const contacts =
    liste.data?.correspondances.filter((contact) => !traites.includes(contact.contactId)) ?? [];
  return (
    <section className={styles.contenu} aria-label={t('profils.aPrevenir')}>
      <p>{t('profils.aucunEnvoi')}</p>
      {liste.isLoading && <p role="status">{t('commun:chargement')}</p>}
      {liste.isError && (
        <p role="alert">
          {estApiError(liste.error) ? liste.error.message : t('commun:erreurReseau')}
        </p>
      )}
      {liste.data && (
        <>
          <p>
            {t('profils.traites', {
              prevenus: liste.data.prevenus,
              refuses: liste.data.pasPourLui,
            })}
          </p>
          {contacts.length === 0 ? (
            <p>{t('profils.aucunContact')}</p>
          ) : (
            <div className={styles.tableauEnveloppe}>
              <table className={styles.tableau}>
                <thead>
                  <tr>
                    <th scope="col">{t('profils.contact')}</th>
                    <th scope="col">{t('profils.raisonsTitre')}</th>
                    <th scope="col">{t('profils.score')}</th>
                    {peutEcrire && <th scope="col">{t('profils.gestes')}</th>}
                  </tr>
                </thead>
                <tbody>
                  {contacts.map((contact) => (
                    <tr key={contact.contactId}>
                      <td>
                        <Link to={`/${galerieSlug}/contacts/${contact.contactId}`}>
                          {contact.nom}
                        </Link>
                      </td>
                      <td>
                        <RaisonsCorrespondance
                          galerieSlug={galerieSlug}
                          raisons={contact.raisons}
                        />
                      </td>
                      <td>{contact.scoreInteret ?? '—'}</td>
                      {peutEcrire && (
                        <td>
                          <div className={styles.gestes}>
                            <Button
                              size="sm"
                              onClick={() => setGeste({ contact, type: 'prevenu' })}
                            >
                              {t('profils.prevenu')}
                            </Button>
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => setGeste({ contact, type: 'pasPourLui' })}
                            >
                              {t('profils.pasPourLui')}
                            </Button>
                            {galerieAModule(galerie, 'pipeline-ventes') && (
                              <Link
                                to={`/${galerieSlug}/pipeline?nouveau=1&contactId=${contact.contactId}&oeuvreId=${oeuvreId}`}
                              >
                                {t('profils.creerAffaire')}
                              </Link>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
      {peutEcrire && (
        <Button
          variant="secondary"
          loading={recalcul.isPending}
          onClick={() =>
            recalcul.mutate(undefined, { onSuccess: () => setRetour(t('profils.recalculEnfile')) })
          }
        >
          {t('profils.recalculer')}
        </Button>
      )}
      {recalcul.isError && (
        <p role="alert">
          {estApiError(recalcul.error) ? recalcul.error.message : t('commun:erreurReseau')}
        </p>
      )}
      <RetourGeste message={retour} />
      {peutEcrire && geste && (
        <DialogueGeste
          key={`${geste.contact.contactId}-${geste.type}`}
          oeuvreId={oeuvreId}
          contact={geste.contact}
          type={geste.type}
          onFermer={() => setGeste(null)}
          onTraite={() => {
            setTraites((precedent) => [...precedent, geste.contact.contactId]);
            setRetour(
              t(geste.type === 'prevenu' ? 'profils.retourPrevenu' : 'profils.retourPasPourLui', {
                nom: geste.contact.nom,
              }),
            );
            setGeste(null);
          }}
        />
      )}
    </section>
  );
}

function DialogueGeste({
  oeuvreId,
  contact,
  type,
  onFermer,
  onTraite,
}: {
  oeuvreId: string;
  contact: CorrespondanceContact;
  type: 'prevenu' | 'pasPourLui';
  onFermer: () => void;
  onTraite: () => void;
}) {
  const { t } = useTranslation('regie');
  const { galerieSlug } = useShellContext();
  const [canal, setCanal] = useState<PrevenirProfil['canal']>('email');
  const [note, setNote] = useState('');
  const [exclureArtiste, setExclureArtiste] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const prevenir = useGesteProfil(galerieSlug, (corps: PrevenirProfil, appel) =>
    profilsApi.prevenir(galerieSlug, oeuvreId, contact.contactId, corps, appel),
  );
  const refuser = useGesteProfil(galerieSlug, (corps: PasPourLuiProfil, appel) =>
    profilsApi.pasPourLui(galerieSlug, oeuvreId, contact.contactId, corps, appel),
  );
  const enCours = prevenir.isPending || refuser.isPending;
  async function confirmer() {
    setErreur(null);
    try {
      if (type === 'prevenu') await prevenir.mutateAsync({ canal, note });
      else await refuser.mutateAsync({ exclureArtiste, note });
      onTraite();
    } catch (cause) {
      setErreur(estApiError(cause) ? cause.message : t('commun:erreurReseau'));
    }
  }
  return (
    <Dialog
      open
      onOpenChange={(ouvert) => {
        if (!ouvert && !enCours) onFermer();
      }}
      title={t(`profils.${type}`)}
      description={contact.nom}
      closeLabel={t('commun:actions.fermer')}
      footer={
        <>
          <Button variant="ghost" disabled={enCours} onClick={onFermer}>
            {t('commun:actions.annuler')}
          </Button>
          <Button loading={enCours} onClick={() => void confirmer()}>
            {t('commun:actions.confirmer')}
          </Button>
        </>
      }
    >
      <div className={styles.formulaire}>
        {type === 'prevenu' ? (
          <Field htmlFor="profil-canal" label={t('profils.canal')}>
            <Select
              id="profil-canal"
              value={canal}
              onValueChange={(valeur) => setCanal(valeur as PrevenirProfil['canal'])}
              options={(['email', 'telephone', 'rendez_vous', 'autre'] as const).map((code) => ({
                value: code,
                label: t(`profils.canaux.${code}`),
              }))}
            />
          </Field>
        ) : (
          <Checkbox
            id="profil-exclure-artiste"
            label={t('profils.exclureArtiste')}
            checked={exclureArtiste}
            onCheckedChange={(valeur) => setExclureArtiste(valeur === true)}
          />
        )}
        <Field htmlFor="profil-note" label={t('profils.notes')}>
          <Textarea
            id="profil-note"
            value={note}
            maxLength={4000}
            onChange={(event) => setNote(event.target.value)}
          />
        </Field>
        {erreur && <p role="alert">{erreur}</p>}
      </div>
    </Dialog>
  );
}
